import moment from 'moment';
import countries from 'i18n-iso-countries';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { ContactType } from 'flow/utils/extension-utils';

export const getName = (loggedCall) => {
    if (!loggedCall) {
        throw Error('Invalid loggedCall parameter');
    }

    const firstName = loggedCall.getIn(['contact', 'firstName']);
    const lastName = loggedCall.getIn(['contact', 'lastName']);

    if (firstName && !lastName) {
        return firstName;
    }

    if (!firstName && lastName) {
        return lastName;
    }

    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    }

    return undefined;
};

export const getDepartment = (loggedCall) => {
    if (!loggedCall) {
        throw Error('Invalid loggedCall parameter');
    }

    const department = loggedCall.getIn(['contact', 'department']);
    const isBound =
        loggedCall.getIn(['contact', 'type']) === ContactType.bound.key;
    if (department && isBound) {
        return department;
    }

    return undefined;
};

const getNumber = (loggedCall) => {
    const loggedCallPhoneNumber = loggedCall.getIn([
        'telephoneNumber',
        'number',
    ]);
    const voiceMailPhoneNumber = loggedCall.getIn(['phoneNumber', 'number']);

    return loggedCallPhoneNumber || voiceMailPhoneNumber;
};

const isE164Number = (loggedCall) => {
    const loggedCallType = loggedCall.getIn(['telephoneNumber', 'type']);
    const voiceMailType = loggedCall.getIn(['phoneNumber', 'type']);

    return (loggedCallType || voiceMailType) === 'e164';
};

export const getCountryCode = (loggedCall) => {
    if (!isE164Number(loggedCall)) {
        return undefined;
    }
    const loggedCallCountryCode = loggedCall.getIn([
        'telephoneNumber',
        'countryCode',
    ]);
    const voiceMailCountryCode = loggedCall.getIn([
        'phoneNumber',
        'countryCode',
    ]);

    return loggedCallCountryCode || voiceMailCountryCode;
};

export const getLoggedCallDisplayText = (
    loggedCall,
    loggedInUserCountryCode,
    title,
) => {
    if (!loggedCall) {
        throw Error('Invalid loggedCall parameter');
    }

    const name = getName(loggedCall);
    if (name && !title) {
        return name;
    }

    const department = getDepartment(loggedCall);
    if (department && title !== department) {
        return department;
    }

    const company = loggedCall.getIn(['contact', 'companyName']);
    if (company && title !== company) {
        return company;
    }

    const number = getNumber(loggedCall);
    const isTechnicalNumber =
        number &&
        loggedCall.getIn(['number', 'type'], '') === 'technical_number';
    let prettyformattedNumber = '';
    if (!isTechnicalNumber) {
        prettyformattedNumber =
            number && isE164Number(loggedCall)
                ? prettyFormatE164Number(number, loggedInUserCountryCode)
                : number;
    }

    if (prettyformattedNumber && prettyformattedNumber !== title) {
        return prettyformattedNumber;
    }

    const countryCode = getCountryCode(loggedCall);
    if (countryCode && title && title !== countryCode) {
        return countries.getName(countryCode, moment.locale());
    }

    return null;
};

export const getLoggedCallDisplayTextTW = (
    loggedCall,
    numberWrapper,
    loggedInUserCountryCode,
    title,
) => {
    if (!loggedCall) {
        throw Error('Invalid loggedCall parameter');
    }

    const name = getName(loggedCall);
    if (name && !title) {
        return name;
    }

    if (!title) {
        const department = getDepartment(loggedCall);
        if (department && title !== department) {
            return department;
        }

        const company = loggedCall.getIn(['contact', 'companyName']);
        if (company && title !== company) {
            return company;
        }
    }

    const number = getNumber(loggedCall);
    const isTechnicalNumber =
        number &&
        loggedCall.getIn(['number', 'type'], '') === 'technical_number';
    let prettyformattedNumber = '';
    if (!isTechnicalNumber) {
        prettyformattedNumber =
            number && isE164Number(loggedCall)
                ? prettyFormatE164Number(number, loggedInUserCountryCode)
                : number;
    }

    if (prettyformattedNumber && prettyformattedNumber !== title) {
        return numberWrapper(prettyformattedNumber);
    }

    const countryCode = getCountryCode(loggedCall);
    if (countryCode && title && title !== countryCode) {
        return countries.getName(countryCode, moment.locale());
    }

    return null;
};

export const getLoggedCallTitle = (
    loggedCall,
    i18next,
    loggedInUserCountryCode,
) => {
    if (!i18next) {
        throw Error('Invalid i18next parameter');
    }

    const title = getLoggedCallDisplayText(loggedCall, loggedInUserCountryCode);

    return title || i18next.t('call.hidden_number');
};

export const getLoggedCallTitleTW = (
    loggedCall,
    i18next,
    numberWrapper,
    loggedInUserCountryCode,
) => {
    if (!i18next) {
        throw Error('Invalid i18next parameter');
    }

    const title = getLoggedCallDisplayTextTW(
        loggedCall,
        numberWrapper,
        loggedInUserCountryCode,
    );

    return title || i18next.t('call.hidden_number');
};

export const getLoggedCallSubtitle = (
    loggedCall,
    i18next,
    loggedInUserCountryCode,
    title,
) => {
    if (!i18next) {
        throw Error('Invalid i18next parameter');
    }

    if (!title) {
        throw Error('title must be defined');
    }

    const subtitle = getLoggedCallDisplayText(
        loggedCall,
        loggedInUserCountryCode,
        title,
    );

    return subtitle || i18next.t('Contact.Unknown');
};

export const getLoggedCallSubtitleTW = (
    loggedCall,
    i18next,
    numberWrapper,
    loggedInUserCountryCode,
    title,
) => {
    if (!i18next) {
        throw Error('Invalid i18next parameter');
    }

    if (!title) {
        throw Error('title must be defined');
    }

    const subtitle = getLoggedCallDisplayTextTW(
        loggedCall,
        numberWrapper,
        loggedInUserCountryCode,
        title,
    );

    return subtitle || i18next.t('Contact.Unknown');
};
